import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Users,
  Star,
  DollarSign,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import {
  mockReviews,
  mockVerifiedPayouts,
  mockUnlistedFirms,
  mockActivities,
  getUserById,
  getFirmById,
  getPendingReviews,
  getPendingPayouts,
} from '@/data/mock';
import { getAllUser } from '@/services/user';
import { useAuth } from '@/authContext/AuthContext';

const TIER_COLORS = {
  'Starter': '#94a3b8',
  'Trader': '#3b82f6',
  'Pro Trader': '#8b5cf6',
  'Elite Trader': '#f59e0b',
};

export const DashboardPage = () => {
  const navigate = useNavigate();

  // const [propFirms,setPropFirms]=useState()
  const {fetchUsers,users,fetchPropFirms,propFirms}=useAuth()

  const pendingReviews = getPendingReviews();
  const pendingPayouts = getPendingPayouts();

useEffect(() => {
    fetchUsers();
    fetchPropFirms();
  }, []);



  // useEffect(() => {
  //   const fetchPropFirms = async () => {
  //     try {
  //       setUsersLoading(true);
  //       const res = await getAllUser();
  //       // API shape is usually { success, data: [...] }
  //       const list = res?.data || (Array.isArray(res) ? res : []);
  //       setUsers(list);
  //     } catch (error) {
  //       console.error(error);
  //     } finally {
  //       setUsersLoading(false);
  //     }
  //   };

  //   fetchUsers();
  // }, []);

  // Rating distribution data
  const ratingDistribution = [
    { rating: '1★', count: mockReviews.filter((r) => r.rating === 1).length },
    { rating: '2★', count: mockReviews.filter((r) => r.rating === 2).length },
    { rating: '3★', count: mockReviews.filter((r) => r.rating === 3).length },
    { rating: '4★', count: mockReviews.filter((r) => r.rating === 4).length },
    { rating: '5★', count: mockReviews.filter((r) => r.rating === 5).length },
  ];

  // Tier distribution
  // const tierDistribution = [
  //   { name: 'Starter', value: users.filter((u) => u.tier === 'Starter').length },
  //   { name: 'Trader', value: users.filter((u) => u.tier === 'Trader').length },
  //   { name: 'Pro Trader', value: users.filter((u) => u.tier === 'Pro Trader').length },
  //   { name: 'Elite Trader', value: users.filter((u) => u.tier === 'Elite Trader').length },
  // ];

  // const totalPointsIssued = users.reduce((sum, user) => sum + user.points, 0);

  // Recent reviews
  const recentReviews = [...mockReviews]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  // Recent payouts
  const recentPayouts = [...mockVerifiedPayouts]
    .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
    .slice(0, 5);

  // Unlisted firms
  const unlistedCount = mockUnlistedFirms.filter((f) => f.status === 'Unlisted').length;
  const warningCount = mockUnlistedFirms.filter((f) => f.status === 'Warning').length;
  const delistedCount = mockUnlistedFirms.filter((f) => f.status === 'Delisted').length;
  const recentUnlisted = [...mockUnlistedFirms]
    .sort((a, b) => new Date(b.dateFlagged) - new Date(a.dateFlagged))
    .slice(0, 3);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Overview of your PropFirm admin panel"
      />

      {/* KPI Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatsCard
          title="Total Firms"
          value={propFirms?.length}
          trend="↑ +3"
          trendLabel="this month"
          icon={Building2}
          iconBg="bg-blue-100 text-blue-600"
        />
        <StatsCard
          title="Total Users"
          value={users?.length.toLocaleString()}
          trend="↑ +84"
          trendLabel="per week"
          icon={Users}
          iconBg="bg-purple-100 text-purple-600"
        />
        <StatsCard
          title="Pending Reviews"
          value={pendingReviews.length}
          trendLabel="Needs review"
          icon={Star}
          iconBg="bg-amber-100 text-amber-600"
          onClick={() => navigate('/dashboard/reviews')}
        />
        <StatsCard
          title="Pending Payouts"
          value={pendingPayouts.length}
          trendLabel="Needs verify"
          icon={DollarSign}
          iconBg="bg-emerald-100 text-emerald-600"
          onClick={() => navigate('/dashboard/payouts')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-slate-800">
              Rating Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ratingDistribution}>
                <XAxis dataKey="rating" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-slate-800">
              Loyalty Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                {/* <Pie
                  data={tierDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {tierDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={TIER_COLORS[entry.name]} />
                  ))}
                </Pie> */}
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-center mt-2">
              <p className="text-sm text-muted-foreground">Total Points Issued</p>
              {/* <p className="text-2xl font-bold text-slate-900">
                {totalPointsIssued.toLocaleString()}
              </p> */}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-slate-800">
              Unlisted Firms Watch
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Unlisted</span>
                <span className="font-medium">{unlistedCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Warning</span>
                <span className="font-medium text-amber-600">{warningCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Delisted</span>
                <span className="font-medium text-red-600">{delistedCount}</span>
              </div>
            </div>
            <div className="space-y-2 border-t pt-3">
              {recentUnlisted.map((firm) => (
                <div key={firm.id} className="flex items-center gap-2">
                  <StatusBadge status={firm.status} />
                  <span className="text-xs text-slate-600 truncate flex-1">
                    {firm.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Reviews */}
      <Card className="rounded-xl border-slate-200 shadow-sm mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-medium text-slate-800">
            Recent Reviews
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/reviews')}
          >
            View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentReviews.map((review) => {
              const user = getUserById(review.userId);
              const firm = getFirmById(review.firmId);
              return (
                <div
                  key={review.id}
                  className="flex items-start gap-4 pb-4 border-b last:border-0 last:pb-0"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <span className="text-slate-400">•</span>
                      <div className="flex items-center gap-1">
                        <img
                          src={firm?.logo}
                          alt={firm?.name}
                          className="h-4 w-4 rounded"
                        />
                        <span className="text-sm text-slate-600">{firm?.name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={`review-${review.id}-star-${i}`}
                            className={`h-3 w-3 ${
                              i < review.rating
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <StatusBadge status={review.status} />
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {review.reviewText}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(review.createdAt), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Payouts & Activity Feed */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-medium text-slate-800">
              Recent Verified Payouts
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard/payouts')}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentPayouts.map((payout) => {
                const user = getUserById(payout.userId);
                const firm = getFirmById(payout.firmId);
                return (
                  <div key={payout.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.avatar} />
                        <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {user?.name}
                        </p>
                        <p className="text-xs text-slate-500">{firm?.name}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-sm font-semibold text-slate-900">
                        ${payout.amount.toLocaleString()}
                      </p>
                      <StatusBadge status={payout.status} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-slate-800">
              Activity Feed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {mockActivities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-2">
                  <div
                    className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
                      activity.color === 'blue'
                        ? 'bg-blue-500'
                        : activity.color === 'green'
                        ? 'bg-emerald-500'
                        : activity.color === 'purple'
                        ? 'bg-purple-500'
                        : activity.color === 'red'
                        ? 'bg-red-500'
                        : activity.color === 'amber'
                        ? 'bg-amber-500'
                        : 'bg-slate-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{activity.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(activity.timestamp), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
